/**
 * Utility برای defer کردن heavy processing تا UI block نشود
 */
import { useState, useEffect } from 'react';

// Defer heavy processing with requestIdleCallback fallback
export const deferProcessing = (callback, timeout = 100) => {
  return new Promise((resolve) => {
    if (typeof window.requestIdleCallback === 'function') {
      // Use requestIdleCallback if available (Chrome, Edge)
      window.requestIdleCallback(() => {
        try {
          const result = callback();
          resolve(result);
        } catch (error) {
          console.error('Error in deferred processing:', error);
          resolve(null);
        }
      }, { timeout });
    } else {
      // Fallback for Safari, Firefox
      setTimeout(() => {
        try {
          const result = callback();
          resolve(result);
        } catch (error) {
          console.error('Error in deferred processing:', error);
          resolve(null);
        }
      }, 16); // Next frame
    }
  });
};

// Break heavy processing into chunks
export const processInChunks = async (items, processFunction, chunkSize = 10) => {
  const results = [];
  
  for (let i = 0; i < items.length; i += chunkSize) {
    const chunk = items.slice(i, i + chunkSize);
    
    // Process chunk
    const chunkResults = await deferProcessing(() => {
      return chunk.map(processFunction);
    });
    
    if (chunkResults) {
      results.push(...chunkResults);
    }
    
    // Allow UI to update between chunks
    if (i + chunkSize < items.length) {
      await new Promise(resolve => setTimeout(resolve, 0));
    }
  }
  
  return results;
};

// Hook for deferred state
export const useDeferredProcessing = (processor, dependencies = []) => {
  const [result, setResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  useEffect(() => {
    if (!processor) return;
    
    setIsProcessing(true);
    
    deferProcessing(processor).then((processedResult) => {
      setResult(processedResult);
      setIsProcessing(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);
  
  return { result, isProcessing };
};
